import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
} from 'react';

import {
  type Column,
  type ColumnVisibilityMenuBaseProps,
} from './ColumnVisibilityMenu.types';

type ColumnPinningConfigInternal = {
  leftPinned?: readonly string[];
  rightPinned?: readonly string[];
};

type ColumnVisibilityMenuContextValue = Omit<
  ColumnVisibilityMenuBaseProps<string, string>,
  'columnPinning'
> & {
  columnPinning: ColumnPinningConfigInternal;
  maxAdditionalColumnsReached: boolean;
  maxAdditionalColumns: number;
  permanentColumns: readonly string[];
  permanentColumnsSet: Set<string>;
  flattenedColumnOptions: { readonly [id: string]: Column };
};

interface ColumnVisibilityMenuContextProps<
  ColumnGroupIds extends string,
  ColumnIds extends string,
> extends ColumnVisibilityMenuBaseProps<ColumnGroupIds, ColumnIds>,
    PropsWithChildren {}

const ColumnVisibilityMenuContext =
  createContext<ColumnVisibilityMenuContextValue | null>(null);

export const ColumnVisibilityMenuContextProvider = <
  ColumnGroupIds extends string,
  ColumnIds extends string,
>({
  children,
  maxAdditionalColumns,
  columnOptions,
  columnPinning = {
    leftPinned: [],
    rightPinned: [],
  },
  permanentColumns,
  columns,
  onColumnsChange,
  ...props
}: ColumnVisibilityMenuContextProps<ColumnGroupIds, ColumnIds>) => {
  const numPermanentColumns = permanentColumns?.length ?? 0;
  const maxAdditionalColumnsReached =
    maxAdditionalColumns !== undefined
      ? columns.length >= maxAdditionalColumns + numPermanentColumns
      : false;

  /**
   * Create a set of permanent columns for easier lookup.
   */
  const permanentColumnsSet = useMemo(
    () => new Set(permanentColumns),
    [permanentColumns],
  );

  /**
   * Flatten the column options into a single object for easier access.
   */
  const flattenedColumnOptions = useMemo(() => {
    const flatColumns: Record<string, Column> = {};

    for (const columnGroup of columnOptions) {
      for (const column of columnGroup.columns) {
        flatColumns[column.id] = column;
      }
    }

    return flatColumns;
  }, [columnOptions]);

  /**
   * Filter out columns that are not in `columns` and are `permanentColumns`
   * from the list of selectable columns.
   */
  const allowedColumnOptions = useMemo(() => {
    return columnOptions
      .map((columnGroup) => {
        const allowedColumnOptions = columnGroup.columns.filter(
          (column) => !permanentColumnsSet.has(column.id),
        );

        return {
          ...columnGroup,
          columns: allowedColumnOptions,
        };
      })
      .filter((columnGroup) => columnGroup.columns.length > 0);
  }, [columnOptions, permanentColumnsSet]);

  const applyPinning = useCallback(
    (visibleColumns: string[]) => {
      const unpinnedVisibleColumns = visibleColumns.filter(
        (col) =>
          !columnPinning.leftPinned?.includes(col as ColumnIds) &&
          !columnPinning.rightPinned?.includes(col as ColumnIds),
      );
      return [
        ...(columnPinning.leftPinned?.filter((col) =>
          visibleColumns.includes(col),
        ) ?? []),
        ...unpinnedVisibleColumns,
        ...(columnPinning.rightPinned?.filter((col) =>
          visibleColumns.includes(col),
        ) ?? []),
      ] as ColumnIds[];
    },
    [columnPinning],
  );

  const handleOnColumnsChange = (newColumns: string[]) => {
    onColumnsChange(applyPinning(newColumns));
  };

  /**
   * Never allow a situation where `columns` state does not contain all
   * of `permanentColumns`.
   */
  useLayoutEffect(() => {
    const combinedColumnsSet = new Set([...columns, ...permanentColumnsSet]);

    // this means `permanentColumns` contains something that is not already in `columns`
    if (combinedColumnsSet.size > columns.length) {
      const newColumns = [...combinedColumnsSet];
      onColumnsChange(applyPinning(newColumns));
    }
  }, [columns, permanentColumnsSet, applyPinning]);

  return (
    <ColumnVisibilityMenuContext.Provider
      value={{
        permanentColumns: permanentColumns ?? [],
        permanentColumnsSet,
        maxAdditionalColumns: maxAdditionalColumns ?? 0,
        maxAdditionalColumnsReached,
        flattenedColumnOptions,
        columnOptions: allowedColumnOptions,
        columnPinning: columnPinning as ColumnPinningConfigInternal,
        columns,
        onColumnsChange: handleOnColumnsChange,
        ...props,
      }}
    >
      {children}
    </ColumnVisibilityMenuContext.Provider>
  );
};

export const useColumnVisibilityMenuContext = () => {
  const context = useContext(ColumnVisibilityMenuContext);

  if (!context) {
    throw new Error(
      'useColumnVisibilityMenuContext must be used within a ColumnVisibilityMenuProvider',
    );
  }

  return context;
};
